import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PlusCircle, Eye, Trash2, FileText, Download } from 'lucide-react';
import { getRiskProfiles, deleteRiskProfile } from '../utils/api';
import { downloadBlankRiskProfileForm } from '../utils/blankRiskProfileForm';
import { ShareLinkButton } from '../components/ShareLinkButton';

const RiskProfiling = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      setProfiles(await getRiskProfiles());
    } catch (err) {
      console.error('Error loading risk profiles:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this risk profile?')) {
      try {
        await deleteRiskProfile(id);
        loadProfiles();
      } catch (err) {
        alert('Failed to delete: ' + err.message);
      }
    }
  };

  const getRiskColor = (profile) => {
    switch (profile) {
      case 'Conservative':
        return 'bg-blue-100 text-blue-700';
      case 'Moderate':
        return 'bg-amber-100 text-amber-700';
      case 'Aggressive':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Investor Risk Profiling</h1>
            <p className="text-slate-600">Assess investor risk tolerance and create personalized profiles</p>
          </div>
          <div className="flex items-center gap-3">
            <ShareLinkButton kind="risk-profile" label="Send Self-Fill Link" className="border-blue-500 text-blue-700 hover:bg-blue-50" />
            <Button
              onClick={() => navigate('/admin/risk-profiling/create')}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-new-profile-btn"
            >
              <PlusCircle className="mr-2" size={20} />
              Create New Profile
            </Button>
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div>
            <p className="font-semibold text-amber-900">Investor not available online?</p>
            <p className="text-sm text-slate-700">
              Download a printable blank form for the investor to fill and sign offline.
            </p>
          </div>
          <Button
            onClick={downloadBlankRiskProfileForm}
            variant="outline"
            className="border-amber-500 text-amber-900 hover:bg-amber-100"
            data-testid="download-blank-form-btn"
          >
            <Download className="mr-2" size={18} />
            Download Blank Form
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText size={24} />
              All Risk Profiles
            </CardTitle>
          </CardHeader>
          <CardContent>
            {profiles.length === 0 ? (
              <div className="text-center py-12">
                <FileText size={64} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600 text-lg mb-4">No risk profiles created yet</p>
                <Button
                  onClick={() => navigate('/admin/risk-profiling/create')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <PlusCircle className="mr-2" size={20} />
                  Create First Profile
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Investor Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Age
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Risk Profile
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Created Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {profiles.map((profile) => (
                      <tr key={profile.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-slate-900">{profile.investor_name}</div>
                          <div className="text-sm text-slate-500">{profile.investor_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                          {profile.age}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getRiskColor(profile.risk_profile)}`}>
                            {profile.risk_profile}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                          {profile.total_score}/100
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          {new Date(profile.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/admin/risk-profiling/view/${profile.id}`)}
                            >
                              <Eye size={16} className="mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(profile.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/dashboard')}
          >
            ← Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RiskProfiling;
